import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/select'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import {
  useUpdateUserPdfConfigMutation,
  useUserInfoQuery,
} from '@/store/api/Users/Users.query'

export const ReportSettings = () => {
  const { t } = useTranslation()
  const { data, isLoading } = useUserInfoQuery()
  const [language, setLanguage] = useState<string>(data?.pdfLanguage || 'en')

  const [updateUserPdfConfig, { isLoading: isLoadingUpdateUserPdfConfig }] =
    useUpdateUserPdfConfigMutation()

  const handleUpdateUserData = async () => {
    try {
      await updateUserPdfConfig({
        pdfLanguage: language,
      }).unwrap()
      toast.success(t('feedback.toast.pdfUpdated'))
    } catch (error) {
      toast.error(t('feedback.toast.errorSavingChanges'))
      catchWithSentry(error)
    }
  }

  if (isLoading) {
    return <LottieSpinner width={320} height={320} text={t('common.loading')} />
  }

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage)
  }

  return (
    <div>
      <div className="flex flex-col mb-6">
        <p className="text-neutralColor text-xl font-semibold tracking-wide font-satoshi mb-4">
          {t('dashboard.settings.reportSettings.language')}
        </p>
        <p className="text-neutralColor80 text-sm font-medium mb-2">
          {t('dashboard.settings.reportSettings.reportingLanguageText')}
        </p>
        <div className="flex gap-2">
          <Select onValueChange={handleLanguageChange}>
            <SelectTrigger className="rounded-3xl w-full lg:w-2/6">
              {language === 'es'
                ? t('dashboard.settings.spanish')
                : t('dashboard.settings.english')}
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel className="px-2 py-1.5 text-textNormal text-base font-medium tracking-tight">
                  {t('dashboard.settings.selectLanguage')}
                </SelectLabel>
                <SelectItem value="es">
                  {t('dashboard.settings.spanish')}
                </SelectItem>
                <SelectItem value="en">
                  {t('dashboard.settings.english')}
                </SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex lg:justify-end lg:items-end">
        <Button
          disabled={language === data?.pdfLanguage}
          type="submit"
          className="mt-6 w-full lg:w-[69px] px-4 py-2"
          isLoading={isLoadingUpdateUserPdfConfig}
          onClick={handleUpdateUserData}
        >
          {t('dashboard.settings.reportSettings.save')}
        </Button>
      </div>
    </div>
  )
}
